import React, { useEffect, useState, useCallback, useContext, useRef } from 'react';
import styled from 'styled-components';
import { SocketContext } from '../context/SocketContext';
import { AuthContext } from '../context/AuthContext';
import { useCall } from '../context/CallContext';
import CallingScreen from './CallingScreen';
import { FocusableSection, FocusableButton, useBackHandler, setFocus } from '../components/Focusable';
import { useToast } from '../context/ToastContext';

// [Styled components identiques - pas de changement]
const MenuButton = styled.button`
  position: fixed;
  top: 20px;
  left: 20px;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  display: ${props => props.$show ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  z-index: 999;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(99, 44, 228, 0.4);

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(99, 44, 228, 0.6);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    top: 15px;
    left: 15px;
    width: 45px;
    height: 45px;
  }
`;

const MenuLine = styled.div`
  width: 25px;
  height: 3px;
  background: #fff;
  border-radius: 2px;
  transition: all 0.3s ease;
`;

const PiPContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 320px;
  height: 240px;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  z-index: 2000;
  cursor: pointer;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8);
  border: 2px solid #632ce4;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 12px 40px rgba(99, 44, 228, 0.6);
  }

  @media (max-width: 768px) {
    width: 200px;
    height: 150px;
    bottom: 15px;
    right: 15px;
    border-radius: 8px;
  }
`;

const PiPVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PiPLocalVideo = styled.video`
  position: absolute;
  bottom: 10px;
  right: 10px;
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid #fff;
  transform: scaleX(-1);

  @media (max-width: 768px) {
    width: 60px;
    height: 45px;
    bottom: 8px;
    right: 8px;
    border-radius: 6px;
  }
`;

const PiPLabel = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '📞';
  }

  @media (max-width: 768px) {
    top: 8px;
    left: 8px;
    padding: 0.3rem 0.6rem;
    font-size: 0.7rem;
    border-radius: 12px;
  }
`;

const PiPEndButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e74c3c;
  color: #fff;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  transition: all 0.3s ease;
  z-index: 10;

  &:hover {
    background: #c0392b;
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    top: 8px;
    right: 8px;
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
`;

const ControlsContainer = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 2rem;
  z-index: 100;

  @media (max-width: 768px) {
    bottom: 30px;
    gap: 1.5rem;
  }
`;

const ControlButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);

  ${props => props.danger ? `
    background: linear-gradient(135deg, #e74c3c, #c0392b);
    color: #fff;
  ` : `
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
  `}

  &:hover {
    transform: scale(1.1);
    box-shadow: ${props => props.danger
      ? '0 6px 20px rgba(231, 76, 60, 0.5)'
      : '0 6px 20px rgba(255, 255, 255, 0.3)'};
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
`;

const VideoCall = ({ sidebar, setCurrentPage, onEndCall, isPiP = false, toggleSidebar }) => {
  const { socket } = useContext(SocketContext);
  const { user } = useContext(AuthContext);
  const {
    callData,
    isCallConnected,
    markCallAsConnected,
    localStreamRef,
    remoteStreamRef,
    peerConnectionRef,
    screenStreamRef,
    callStartedRef,
    hasSetupConnectionRef,
    endCall: contextEndCall,
  } = useCall();

  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [callEnded, setCallEnded] = useState(false);
  const [streamUpdate, setStreamUpdate] = useState(0);
  const [isWaitingForAnswer, setIsWaitingForAnswer] = useState(false);

  const controlsRef = useRef(null);
  const videoContainerRef = useRef(null);
  const callAcceptedRef = useRef(false); // Track si ready-for-call a été reçu/émis
  const driftMonitorRef = useRef(null); // Intervalle de monitoring du drift
  const toast = useToast();

  const roomId = callData?.roomId;
  const config = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };

  const endCall = useCallback(() => {
    console.log('🔴 End call appelé - isCallConnected:', isCallConnected, 'callAccepted:', callAcceptedRef.current);
    console.trace('🔴 Stack trace endCall'); // Pour voir d'où vient l'appel

    if (socket && roomId) {
      // Si l'appel n'est pas encore connecté ET n'a pas été accepté, c'est une annulation
      if (!isCallConnected && !callAcceptedRef.current && callData) {
        console.log('📤 Envoi cancel-call vers', callData.contactEmail);
        socket.emit('cancel-call', {
          targetEmail: callData.contactEmail,
          roomId
        });
      } else {
        // Sinon l'appel a été accepté ou est connecté, c'est une fin d'appel normale
        console.log('📤 Envoi call-ended');
        socket.emit('call-ended', { roomId });
      }
    }

    // Arrêter le monitoring du drift
    if (driftMonitorRef.current) {
      clearInterval(driftMonitorRef.current);
      driftMonitorRef.current = null;
    }

    // Reset la ref pour le prochain appel
    callAcceptedRef.current = false;

    contextEndCall();

    if (onEndCall) {
      onEndCall();
    }
  }, [socket, roomId, isCallConnected, contextEndCall, onEndCall, callData]);
  
  // Gestion du back pour terminer l'appel
  useBackHandler(() => {
    if (!isPiP && isCallConnected && !sidebar) {
      endCall();
    }
  });

  // Focus sur les contrôles vidéo quand l'appel est connecté
  useEffect(() => {
    if (!isPiP && isCallConnected) {
      // Petit délai pour laisser le rendu se faire
      setTimeout(() => {
        setFocus('VIDEO_CALL_CONTROLS');
      }, 100);
    }
  }, [isPiP, isCallConnected]);

  useEffect(() => {
    const videoElements = document.querySelectorAll('video');

    videoElements.forEach(video => {
      if (video.muted && localStreamRef.current && !video.srcObject) {
        video.srcObject = localStreamRef.current;
        console.log('✅ Local video attached');
      }
      else if (!video.muted && remoteStreamRef.current && !video.srcObject) {
        video.srcObject = remoteStreamRef.current;
        // Optimisations temps réel pour la vidéo distante
        video.disableRemotePlayback = true;
        console.log('✅ Remote video attached (temps réel)');
      }
    });
  }, [streamUpdate, isPiP]);

  useEffect(() => {
    if (socket) {
      const handleCallEnded = () => {
        console.log('🔴 Appel terminé par l\'autre partie');
        setCallEnded(true);

        contextEndCall();

        setTimeout(() => {
          setCallEnded(false);
          if (onEndCall) {
            onEndCall(); // Ceci redirige vers contacts ET rouvre la sidebar
          } else if (setCurrentPage) {
            setCurrentPage('contacts');
          }
        }, 4000);
      };

      const handleUserUnavailable = (data) => {
        console.log('❌ Utilisateur non disponible:', data.targetEmail);
        toast.warning(`${data.targetEmail} n'est pas disponible actuellement`, {
          title: 'Contact hors ligne'
        });
        contextEndCall();
        if (setCurrentPage) {
          setCurrentPage('contacts');
        }
      };
      
      socket.on('call-ended', handleCallEnded);
      socket.on('user-unavailable', handleUserUnavailable);
      
      return () => {
        socket.off('call-ended', handleCallEnded);
        socket.off('user-unavailable', handleUserUnavailable);
      };
    }
  }, [socket, contextEndCall, setCurrentPage]);

  // Cleanup du drift monitor au démontage du composant
  useEffect(() => {
    return () => {
      if (driftMonitorRef.current) {
        clearInterval(driftMonitorRef.current);
        driftMonitorRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    console.log('🔄 useEffect call setup - callData:', !!callData, 'callStarted:', callStartedRef.current, 'hasSetup:', hasSetupConnectionRef.current);
    if (callData && !callStartedRef.current && socket && !hasSetupConnectionRef.current) {
      hasSetupConnectionRef.current = true;

      if (callData.isCaller) {
        console.log('📞 Mode: APPELANT');
        initiateCall();
      } else if (callData.isIncoming) {
        console.log('📞 Mode: DESTINATAIRE');
        acceptCall();
      }
    }
  }, [callData, socket]);

  const initiateCall = async () => {
    if (callStartedRef.current || !roomId || !socket) {
      return;
    }

    try {
      callStartedRef.current = true;
      callAcceptedRef.current = false; // Reset pour nouvel appel
      setIsWaitingForAnswer(true);
      console.log('📞 Initiation de l\'appel vers:', callData.contactEmail);

      socket.emit('call-user', {
        roomId,
        callerEmail: user.email,
        targetEmail: callData.contactEmail,
        callerName: user.email.split('@')[0],
        autoAnswer: !!callData.autoAnswer
      });

      socket.emit('join-room', roomId);
      await setupMediaAndPeerConnection(true);
    } catch (error) {
      console.error('Error initiating call:', error);
      endCall();
    }
  };

  const acceptCall = async () => {
    if (callStartedRef.current || !roomId || !socket) {
      console.log('⚠️ acceptCall ignoré - callStarted:', callStartedRef.current, 'roomId:', roomId, 'socket:', !!socket);
      return;
    }

    try {
      console.log('✅ Acceptation de l\'appel - roomId:', roomId);
      callStartedRef.current = true;
      callAcceptedRef.current = true; // Marquer l'appel comme accepté

      console.log('📤 Envoi join-room');
      socket.emit('join-room', roomId);

      console.log('🎥 Démarrage setupMediaAndPeerConnection...');
      await setupMediaAndPeerConnection(false);

      console.log('📤 Envoi ready-for-call');
      socket.emit('ready-for-call', { roomId });
      console.log('✅ ready-for-call envoyé avec succès');
    } catch (error) {
      console.error('❌ Error accepting call:', error);
      endCall();
    }
  };

  // ============================================
  // 📊 MONITORING DU DRIFT VIDÉO
  // ============================================
  // Surveille le jitter buffer toutes les 3s.
  // Si le délai accumulé dépasse 500ms, on force un reset
  // du flux vidéo distant pour resynchroniser.
  const startDriftMonitor = (pc) => {
    // Nettoyer un éventuel moniteur précédent
    if (driftMonitorRef.current) {
      clearInterval(driftMonitorRef.current);
    }

    driftMonitorRef.current = setInterval(async () => {
      if (!pc || pc.connectionState === 'closed') {
        clearInterval(driftMonitorRef.current);
        driftMonitorRef.current = null;
        return;
      }

      try {
        const stats = await pc.getStats();
        stats.forEach((report) => {
          if (report.type === 'inbound-rtp' && report.kind === 'video') {
            const jitterBufferDelay = report.jitterBufferDelay;
            const jitterBufferEmittedCount = report.jitterBufferEmittedCount;

            if (jitterBufferDelay && jitterBufferEmittedCount && jitterBufferEmittedCount > 0) {
              // Délai moyen du jitter buffer en ms
              const avgDelayMs = (jitterBufferDelay / jitterBufferEmittedCount) * 1000;
              console.log(`📊 Jitter buffer vidéo: ${avgDelayMs.toFixed(0)}ms`);

              // Si le drift dépasse 500ms, forcer un reset du flux
              if (avgDelayMs > 500) {
                console.warn(`⚠️ Drift détecté: ${avgDelayMs.toFixed(0)}ms > 500ms - resync du flux vidéo`);
                resetRemoteVideoStream();
              }
            }
          }
        });
      } catch (error) {
        // Silencieux si la connexion est fermée
      }
    }, 3000);
  };

  // Force un détachement/réattachement du flux vidéo distant
  // pour vider le buffer du navigateur et resynchroniser
  const resetRemoteVideoStream = () => {
    const videoElements = document.querySelectorAll('video');
    videoElements.forEach(video => {
      if (!video.muted && remoteStreamRef.current) {
        video.srcObject = null;
        // Réattacher immédiatement pour flush le buffer
        video.srcObject = remoteStreamRef.current;
        video.play().catch(err => console.warn('Video resync play failed:', err.message));
        console.log('🔄 Flux vidéo distant resynchronisé');
      }
    });
  };

  // ============================================
  // 🚀 FONCTION OPTIMISÉE POUR RPI4 ET RPI5
  // ============================================
  const setupMediaAndPeerConnection = async (isInitiator) => {
    console.log('🎥 Configuration optimale RPI4/RPI5 - 720p @ 30fps');
    
    // Configuration vidéo optimale pour RPI4 et RPI5
    // 720p = sweet spot : excellente qualité sur TV, performances OK
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { 
        width: { ideal: 1280 },        // 720p largeur
        height: { ideal: 720 },        // 720p hauteur
        frameRate: { ideal: 30, max: 30 }, // 30fps max
        facingMode: 'user'             // Caméra frontale
      },
      audio: {
        echoCancellation: true,        // Annulation d'écho (utile)
        noiseSuppression: true,        // Suppression de bruit (utile)
        autoGainControl: false,        // Désactivé - évite la compression automatique
        sampleRate: 48000,             // Haute fréquence d'échantillonnage
        sampleSize: 16,                // 16 bits par échantillon
        channelCount: 1,               // Mono suffit pour appels
        latency: 0,                    // Latence minimale
        volume: 1.0                    // Volume maximal
      }
    });
    
    localStreamRef.current = stream;
    console.log('✅ Local stream created - 720p @ 30fps');
    
    // Afficher les capacités réelles obtenues
    const videoTrack = stream.getVideoTracks()[0];
    const videoSettings = videoTrack.getSettings();
    console.log('📹 Résolution réelle:', videoSettings.width, 'x', videoSettings.height, '@', videoSettings.frameRate, 'fps');
    
    setStreamUpdate(prev => prev + 1);
    
    const pc = new RTCPeerConnection(config);
    peerConnectionRef.current = pc;

    // Ajouter les tracks au peer connection
    stream.getTracks().forEach(track => {
      pc.addTrack(track, stream);
      console.log('✅ Track ajouté:', track.kind);
    });

    // ============================================
    // 🎯 OPTIMISATION BITRATE POUR QUALITÉ TV
    // ============================================
    const configureSenderParameters = async () => {
      const senders = pc.getSenders();
      for (const sender of senders) {
        if (sender.track?.kind === 'video') {
          const parameters = sender.getParameters();
          if (!parameters.encodings || parameters.encodings.length === 0) {
            parameters.encodings = [{}];
          }
          parameters.encodings[0].maxBitrate = 2500000;
          parameters.encodings[0].maxFramerate = 30;
          parameters.degradationPreference = 'maintain-framerate';
          parameters.encodings[0].priority = 'high';
          parameters.encodings[0].networkPriority = 'high';
          try {
            await sender.setParameters(parameters);
            console.log('✅ Sender configuré: 2.5 Mbps @ 30fps, maintain-framerate');
          } catch (error) {
            console.error('⚠️ Erreur configuration sender:', error);
          }
        }
      }
    };

    // Appliquer quand la connexion ICE est établie (moment fiable où les senders sont prêts)
    pc.oniceconnectionstatechange = async () => {
      console.log('🔌 ICE Connection State:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        await configureSenderParameters();
      }
      if (pc.iceConnectionState === 'failed') {
        console.error('❌ Connexion ICE échouée');
      }
    };

    // Gestion des ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && socket) {
        socket.emit('ice-candidate', { 
          candidate: event.candidate, 
          roomId
        });
      }
    };

    // Réception du flux distant
    pc.ontrack = (event) => {
      console.log('🎥 Received remote track:', event.track.kind);

      // ============================================
      // 🎯 OPTIMISATION LATENCE CÔTÉ RÉCEPTION
      // ============================================
      // Limiter le jitter buffer pour éviter l'accumulation de délai
      const receiver = event.receiver;
      if (receiver) {
        // playoutDelayHint : délai cible en secondes pour la lecture
        // 0 = latence minimale, le navigateur utilise le strict minimum
        try {
          receiver.playoutDelayHint = 0;
          console.log('✅ playoutDelayHint = 0 sur', event.track.kind);
        } catch (e) {
          console.warn('⚠️ playoutDelayHint non supporté:', e.message);
        }

        // jitterBufferTarget : taille cible du jitter buffer en ms
        // Limiter à 50ms pour garder le flux le plus temps-réel possible
        try {
          if (receiver.jitterBufferTarget !== undefined) {
            receiver.jitterBufferTarget = 50;
            console.log('✅ jitterBufferTarget = 50ms sur', event.track.kind);
          }
        } catch (e) {
          console.warn('⚠️ jitterBufferTarget non supporté:', e.message);
        }
      }

      if (event.streams && event.streams[0]) {
        console.log('📺 Setting remote stream');
        remoteStreamRef.current = event.streams[0];

        // Marquer l'appel comme connecté
        markCallAsConnected();
        setIsWaitingForAnswer(false);

        // Notifier le serveur que l'appel a démarré
        socket.emit('call-started', { roomId });

        // Démarrer le monitoring du drift
        startDriftMonitor(pc);

        setStreamUpdate(prev => prev + 1);
      }
    };

    // Gestion des événements de signalisation
    const handleOffer = async (data) => {
      if (data.sender !== socket.id) {
        try {
          await pc.setRemoteDescription(data.offer);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          socket.emit('answer', { answer, roomId });
          console.log('📤 Answer envoyée');
        } catch (error) {
          console.error('Error handling offer:', error);
        }
      }
    };

    const handleAnswer = async (data) => {
      if (data.sender !== socket.id) {
        try {
          await pc.setRemoteDescription(data.answer);
          console.log('📥 Answer reçue');
        } catch (error) {
          console.error('Error handling answer:', error);
        }
      }
    };

    const handleIceCandidate = async (data) => {
      if (data.sender !== socket.id) {
        // Vérifier que la connexion est encore ouverte avant d'ajouter le candidat
        if (pc.signalingState === 'closed') {
          return;
        }
        try {
          await pc.addIceCandidate(data.candidate);
        } catch (error) {
          // Ignorer les erreurs si la connexion est fermée (candidats en retard)
          if (pc.signalingState !== 'closed') {
            console.error('Error adding ICE candidate:', error);
          }
        }
      }
    };

    socket.on('offer', handleOffer);
    socket.on('answer', handleAnswer);
    socket.on('ice-candidate', handleIceCandidate);

    // Si on est l'initiateur, créer l'offre quand le destinataire est prêt
    if (isInitiator) {
      const handleReadyForCall = async (data) => {
        if (data.roomId === roomId) {
          console.log('✅ Destinataire prêt, appel accepté');
          callAcceptedRef.current = true; // Marquer l'appel comme accepté
          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            socket.emit('offer', { offer, roomId });
            console.log('📤 Offer envoyée');
          } catch (error) {
            console.error('Error creating offer:', error);
          }
          socket.off('ready-for-call', handleReadyForCall);
        }
      };

      socket.on('ready-for-call', handleReadyForCall);
    }
  };

  const toggleScreenShare = async () => {
    if (!peerConnectionRef.current) {
      return;
    }

    try {
      if (!isScreenSharing) {
        const screen = await navigator.mediaDevices.getDisplayMedia({ video: true });
        screenStreamRef.current = screen;
        setIsScreenSharing(true);

        const screenTrack = screen.getVideoTracks()[0];
        screenTrack.onended = () => endScreenShare();
        
        const sender = peerConnectionRef.current.getSenders().find(s => s.track && s.track.kind === 'video');
        if (sender) {
          await sender.replaceTrack(screenTrack);
        }
      } else {
        endScreenShare();
      }
    } catch (error) {
      console.error('Error toggling screen share:', error);
      setIsScreenSharing(false);
    }
  };

  const endScreenShare = async () => {
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
      setIsScreenSharing(false);

      if (localStreamRef.current && peerConnectionRef.current) {
        const videoTrack = localStreamRef.current.getVideoTracks()[0];
        const sender = peerConnectionRef.current.getSenders().find(s => s.track && s.track.kind === 'video');
        if (sender && videoTrack) {
          await sender.replaceTrack(videoTrack);
        }
      }
    }
  };

  const handlePiPClick = () => {
    setCurrentPage('appels');
  };

  if (!socket) {
    return (
      <div style={{ padding: '2rem', color: '#fff' }}>
        <h2>Connexion au serveur...</h2>
      </div>
    );
  }

  if (isWaitingForAnswer && callData && !isPiP) {
    return (
      <CallingScreen 
        contactName={callData.contactName}
        onCancel={endCall}
      />
    );
  }

  if (callEnded && !isPiP) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100dvh',
          background: 'linear-gradient(135deg, #1a1d24 0%, #0a0c10 100%)',
        }}
      >
        <div
          style={{
            textAlign: 'center',
            padding: '3rem',
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <h1 style={{ color: '#fff', fontSize: '2.5rem', marginBottom: '1rem' }}>
            Appel terminé...
          </h1>
        </div>
      </div>
    );
  }

  // Mode Picture-in-Picture
  if (isPiP) {
    return (
      <PiPContainer onClick={handlePiPClick}>
        <PiPVideo autoPlay playsInline />
        <PiPLocalVideo autoPlay playsInline muted />
        <PiPLabel>En appel</PiPLabel>
        <PiPEndButton 
          onClick={(e) => {
            e.stopPropagation();
            endCall();
          }}
        >
          ✕
        </PiPEndButton>
      </PiPContainer>
    );
  }

  // Mode plein écran
  return (
    <FocusableSection focusKey="VIDEO_CALL_CONTROLS">
      <div
        ref={videoContainerRef}
        style={{
          margin: 0,
          padding: 0,
          overflow: 'hidden',
          background: 'black',
          width: sidebar ? 'calc(100dvw - 150px)' : '100dvw',
          height: '100dvh',
          position: 'relative',
          transition: 'all 350ms',
        }}
      >
        {/* Bouton hamburger pour ouvrir la sidebar */}
        <FocusableButton
          as={MenuButton}
          show={!sidebar}
          onPress={toggleSidebar}
        >
          <MenuLine />
          <MenuLine />
          <MenuLine />
        </FocusableButton>

        {/* Vidéo distante - optimisée temps réel */}
        <video
          autoPlay
          playsInline
          ref={(el) => {
            if (el) {
              // Désactiver le buffering navigateur pour le temps réel
              el.disableRemotePlayback = true;
              // Forcer la lecture la plus rapide possible
              if ('latencyHint' in el) el.latencyHint = 0;
            }
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            background: '#000',
          }}
        />
        <video
          autoPlay
          playsInline
          muted
          style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            width: '20vw',
            height: '20vh',
            objectFit: 'cover',
            zIndex: 60,
            borderRadius: '10px',
            border: '2px solid #fff',
            transform: 'scaleX(-1)',
          }}
        />

        <ControlsContainer ref={controlsRef}>
          <FocusableButton
            as={ControlButton}
            onPress={endCall}
            danger
          >
            📞
          </FocusableButton>

          <FocusableButton
            as={ControlButton}
            onPress={toggleScreenShare}
            disabled={!callStartedRef.current}
          >
            {isScreenSharing ? '🖥️' : '📺'}
          </FocusableButton>
        </ControlsContainer>
      </div>
    </FocusableSection>
  );
};

export default VideoCall;